import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type {
  Dostawca, Egzemplarz, Indeks, Lista, Lokalizacja, Ostrzezenie, StanTechniczny,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Miejsce, Plakietka, Pole, PoleTekstowe, Przycisk, Pusto, Wybor,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { data as fData, zlote } from '../lib/format';
import { STANY } from '../lib/slowniki';

const FINANSOWANIE: Record<string, string> = {
  PROJEKT: 'Projekt', DOTACJA: 'Dotacja', SRODKI_WLASNE: 'Środki własne',
  DAROWIZNA: 'Darowizna', INNE: 'Inne',
};

export function Sprzet() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('sprzet.zapis');
  const [szukaj, setSzukaj] = useState('');
  const [stan, setStan] = useState<StanTechniczny | ''>('');
  const [edytowany, setEdytowany] = useState<Egzemplarz | 'nowy' | null>(null);
  const [ostrzezenia, setOstrzezenia] = useState<Ostrzezenie[]>([]);

  const parametry = new URLSearchParams({
    ...(szukaj ? { szukaj } : {}),
    ...(stan ? { condition: stan } : {}),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['sprzet', parametry.toString()],
    queryFn: () => api.get<Lista<Egzemplarz>>(`/sprzet?${parametry}`),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Sprzęt</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Każda sztuka to osobny rekord. Numer CSM nadaje system, numer kwestury przychodzi z księgowości.
          </p>
        </div>
        <PrzyciskExcel dzial="sprzet" parametry={parametry} className="ml-auto" />
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => { setEdytowany('nowy'); setOstrzezenia([]); }}>
            Nowy egzemplarz
          </Przycisk>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Pole
            etykieta="Szukaj"
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
            placeholder="Numer CSM, numer kwestury, numer seryjny, model"
            podpowiedz="Wyszukiwarka przeszukuje obie numeracje jednocześnie"
          />
        </div>
        <div className="min-w-48">
          <Wybor etykieta="Stan techniczny" value={stan} onChange={(e) => setStan(e.target.value as StanTechniczny | '')}>
            <option value="">wszystkie</option>
            {Object.entries(STANY).map(([k, v]) => <option key={k} value={k}>{v.etykieta}</option>)}
          </Wybor>
        </div>
      </div>

      {ostrzezenia.map((o) => (
        <Komunikat key={o.kod} rodzaj="uwaga" tytul="Sprawdź numer kwestury">
          <p>{o.komunikat}</p>
          {o.szczegoly?.egzemplarze && (
            <ul className="mt-2 list-inside list-disc text-sm">
              {o.szczegoly.egzemplarze.map((e) => (
                <li key={e.id}><span className="font-mono">{e.nrCsm}</span>{e.model ? ` — ${e.model}` : ''}</li>
              ))}
            </ul>
          )}
        </Komunikat>
      ))}

      {edytowany && (
        <FormularzEgzemplarza
          key={edytowany === 'nowy' ? 'nowy' : edytowany.id}
          egzemplarz={edytowany === 'nowy' ? null : edytowany}
          zamknij={() => setEdytowany(null)}
          poZapisie={(o) => { setOstrzezenia(o); setEdytowany(null); }}
        />
      )}

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Nr CSM</th>
                    <th className="px-5 py-3 font-medium">Nr kwestury</th>
                    <th className="px-5 py-3 font-medium">Typ / model</th>
                    <th className="px-5 py-3 font-medium">Nr seryjny</th>
                    <th className="px-5 py-3 font-medium">Lokalizacja</th>
                    <th className="px-5 py-3 font-medium">Gwarancja</th>
                    <th className="px-5 py-3 font-medium">Stan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => mozeEdytowac && setEdytowany(e)}
                      className={mozeEdytowac ? 'cursor-pointer hover:bg-kamien-50' : ''}
                    >
                      <td className="px-5 py-3 font-mono text-xs font-medium text-kamien-900">{e.nrCsm}</td>
                      <td className="px-5 py-3 font-mono text-xs text-kamien-600">{e.nrKwestury ?? '—'}</td>
                      <td className="px-5 py-3">
                        <span className="text-kamien-900">{e.item.name}</span>
                        {e.model && <span className="block text-xs text-kamien-500">{e.manufacturer} {e.model}</span>}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-kamien-600">{e.serialNumber ?? '—'}</td>
                      <td className="px-5 py-3 text-xs"><Miejsce lokalizacja={e.currentLocation} /></td>
                      <td className="px-5 py-3 text-kamien-600">{fData(e.warrantyUntil)}</td>
                      <td className="px-5 py-3">
                        <Plakietka ton={STANY[e.condition].ton}>{STANY[e.condition].etykieta}</Plakietka>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-kamien-200 px-5 py-3 text-sm text-kamien-500">
                {lista.data.razem} egzemplarzy
              </p>
            </div>
          ) : (
            <Pusto
              tytul="Brak egzemplarzy"
              opis={szukaj
                ? 'Żaden egzemplarz nie pasuje do wyszukiwania — sprawdź obie numeracje.'
                : 'Zacznij od założenia typu sprzętu w kartotece, potem dodaj poszczególne sztuki.'}
            />
          )}
        </Karta>
      )}
    </div>
  );
}

const PUSTY = {
  itemId: '', nrCsm: '', nrKwestury: '', serialNumber: '', manufacturer: '', model: '',
  purchaseDate: '', purchaseValue: '', fundingSource: '', fundingNote: '', supplierId: '',
  warrantyUntil: '', condition: 'SPRAWNY' as StanTechniczny, currentLocationId: '',
  notes: '', powod: '',
};

const naDzien = (v: string | null) => (v ? v.slice(0, 10) : '');

function FormularzEgzemplarza({ egzemplarz, zamknij, poZapisie }: {
  egzemplarz: Egzemplarz | null;
  zamknij: () => void;
  poZapisie: (o: Ostrzezenie[]) => void;
}) {
  const qc = useQueryClient();
  const widziCeny = useAuth().maUprawnienie('ceny.odczyt');
  const edycja = useEdycjaWspolna<Egzemplarz, typeof PUSTY>({
    encja: 'sprzet', rekord: egzemplarz, pusty: PUSTY,
    doFormularza: (e) => ({
      itemId: String(e.item.id), nrCsm: e.nrCsm,
      nrKwestury: e.nrKwestury ?? '', serialNumber: e.serialNumber ?? '',
      manufacturer: e.manufacturer ?? '', model: e.model ?? '',
      purchaseDate: naDzien(e.purchaseDate),
      purchaseValue: e.purchaseValue ?? '',
      fundingSource: e.fundingSource ?? '', fundingNote: e.fundingNote ?? '',
      supplierId: String(e.supplier?.id ?? ''),
      warrantyUntil: naDzien(e.warrantyUntil),
      condition: e.condition,
      currentLocationId: String(e.currentLocation?.id ?? ''),
      notes: e.notes ?? '', powod: '',
    }),
    wczytaj: (id) => api.get<Egzemplarz>(`/sprzet/${id}`),
    etykiety: {
      nrCsm: 'Numer CSM', nrKwestury: 'Numer kwestury', serialNumber: 'Numer seryjny',
      manufacturer: 'Producent', model: 'Model', purchaseDate: 'Data zakupu', purchaseValue: 'Wartość zakupu',
      fundingSource: 'Źródło finansowania', fundingNote: 'Opis finansowania', supplierId: 'Dostawca',
      warrantyUntil: 'Gwarancja do', condition: 'Stan techniczny', currentLocationId: 'Lokalizacja',
      notes: 'Uwagi', powod: 'Powód zmiany stanu',
    },
    pokazWartosc: (pole, v) => {
      if (pole === 'supplierId') return nazwaZListy(dostawcy.pozycje, v, (d) => d.name);
      if (pole === 'currentLocationId') return nazwaZListy(lokalizacje.pozycje, v, (l) => l.path ?? l.name);
      return undefined;
    },
  });
  const { dane, ustaw: setDane } = edycja;
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const typy = useLista({
    queryKey: ['kartoteka', 'sprzet'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?type=EQUIPMENT&tylkoAktywne=true&naStronie=200'),
  });
  const lokalizacje = useLista({
    queryKey: ['lokalizacje', 'wszystkie'],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>('/lokalizacje?tylkoAktywne=true'),
  });
  const dostawcy = useLista({
    queryKey: ['dostawcy'], queryFn: () => api.get<{ pozycje: Dostawca[] }>('/dostawcy'),
  });
  const podpowiedzNumeru = useQuery({
    queryKey: ['sprzet', 'nastepny-numer'],
    queryFn: () => api.get<{ nrCsm: string }>('/sprzet/nastepny-numer'),
    enabled: !egzemplarz,
  });

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        ...d,
        itemId: Number(d.itemId),
        supplierId: d.supplierId || null,
        currentLocationId: d.currentLocationId || null,
        fundingSource: d.fundingSource || null,
        ...(egzemplarz ? { version: wersja } : {}),
      };
      return egzemplarz
        ? api.put<{ egzemplarz: Egzemplarz; ostrzezenia: Ostrzezenie[] }>(`/sprzet/${egzemplarz.id}`, cialo)
        : api.post<{ egzemplarz: Egzemplarz; ostrzezenia: Ostrzezenie[] }>('/sprzet', cialo);
    }),
    onSuccess: (odp) => {
      void qc.invalidateQueries({ queryKey: ['sprzet'] });
      void qc.invalidateQueries({ queryKey: ['lokalizacja'] });
      edycja.poZapisie(odp.egzemplarz, () => poZapisie(odp.ostrzezenia ?? []));
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  return (
    <Karta
      tytul={egzemplarz ? `Egzemplarz ${egzemplarz.nrCsm}` : 'Nowy egzemplarz'}
      opis={egzemplarz
        ? `Wersja ${egzemplarz.version} · zdarzeń serwisowych: ${egzemplarz._count.serviceEvents}`
        : undefined}
    >
      <form
        className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}
      >
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        <PasekWspolpracy edycja={edycja} />

        <div className="sm:col-span-2 lg:col-span-3">
          <Wybor etykieta="Typ sprzętu z kartoteki" value={dane.itemId} blad={bledy.itemId ?? typy.blad}
            onChange={(e) => setDane({ ...dane, itemId: e.target.value })}
            disabled={!!egzemplarz} required>
            <option value="">— wybierz —</option>
            {typy.pozycje.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
          </Wybor>
        </div>

        {/* Dwie numeracje obok siebie — jedyne miejsce, gdzie się spotykają. */}
        <div className="grid gap-4 sm:col-span-2 lg:col-span-3 lg:grid-cols-2">
          <Pole
            etykieta="Numer ewidencyjny CSM"
            value={dane.nrCsm} blad={bledy.nrCsm}
            onChange={(e) => setDane({ ...dane, nrCsm: e.target.value })}
            disabled={!!egzemplarz}
            placeholder={podpowiedzNumeru.data?.nrCsm ?? ''}
            podpowiedz={egzemplarz
              ? 'Numeru ewidencyjnego nie zmieniamy — jest na etykiecie.'
              : `Zostaw puste, a system nada ${podpowiedzNumeru.data?.nrCsm ?? 'kolejny numer'}. Wypełnij tylko dla sprzętu z wcześniejszą naklejką.`}
          />
          <Pole
            etykieta="Numer środka trwałego (kwestura)"
            value={dane.nrKwestury} blad={bledy.nrKwestury}
            onChange={(e) => setDane({ ...dane, nrKwestury: e.target.value })}
            podpowiedz="Może się powtarzać — komplet bywa jednym środkiem trwałym."
          />
        </div>

        <Pole etykieta="Producent" value={dane.manufacturer} blad={bledy.manufacturer}
          onChange={(e) => setDane({ ...dane, manufacturer: e.target.value })} />
        <Pole etykieta="Model" value={dane.model} blad={bledy.model}
          onChange={(e) => setDane({ ...dane, model: e.target.value })} />
        <Pole etykieta="Numer seryjny" value={dane.serialNumber} blad={bledy.serialNumber}
          onChange={(e) => setDane({ ...dane, serialNumber: e.target.value })} />

        <Pole etykieta="Data zakupu" type="date" value={dane.purchaseDate} blad={bledy.purchaseDate}
          onChange={(e) => setDane({ ...dane, purchaseDate: e.target.value })} />
        {widziCeny ? (
          <Pole etykieta="Wartość (zł)" type="number" step="0.01" min="0" value={dane.purchaseValue}
            blad={bledy.purchaseValue}
            onChange={(e) => setDane({ ...dane, purchaseValue: e.target.value })} />
        ) : <div />}
        <Pole etykieta="Koniec gwarancji" type="date" value={dane.warrantyUntil} blad={bledy.warrantyUntil}
          onChange={(e) => setDane({ ...dane, warrantyUntil: e.target.value })} />

        <Wybor etykieta="Źródło finansowania" value={dane.fundingSource} blad={bledy.fundingSource}
          onChange={(e) => setDane({ ...dane, fundingSource: e.target.value })}>
          <option value="">— nie wskazano —</option>
          {Object.entries(FINANSOWANIE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Wybor>
        <div className="lg:col-span-2">
          <Pole etykieta="Projekt / umowa" value={dane.fundingNote} blad={bledy.fundingNote}
            onChange={(e) => setDane({ ...dane, fundingNote: e.target.value })} />
        </div>

        <Wybor etykieta="Dostawca" value={dane.supplierId} blad={bledy.supplierId ?? dostawcy.blad}
          onChange={(e) => setDane({ ...dane, supplierId: e.target.value })}>
          <option value="">— nie wskazano —</option>
          {dostawcy.pozycje.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Wybor>
        <Wybor etykieta="Lokalizacja" value={dane.currentLocationId}
          blad={bledy.currentLocationId ?? lokalizacje.blad}
          onChange={(e) => setDane({ ...dane, currentLocationId: e.target.value })}>
          <option value="">— nie wskazano —</option>
          {lokalizacje.pozycje.map((l) => (
            <option key={l.id} value={l.id}>{l.code} — {l.path ?? l.name}</option>
          ))}
        </Wybor>
        <Wybor etykieta="Stan techniczny" value={dane.condition} blad={bledy.condition}
          onChange={(e) => setDane({ ...dane, condition: e.target.value as StanTechniczny })}>
          {Object.entries(STANY).map(([k, v]) => <option key={k} value={k}>{v.etykieta}</option>)}
        </Wybor>

        {egzemplarz && egzemplarz.condition !== dane.condition && (
          <div className="sm:col-span-2 lg:col-span-3">
            <Pole
              etykieta="Powód zmiany stanu"
              value={dane.powod} blad={bledy.powod}
              onChange={(e) => setDane({ ...dane, powod: e.target.value })}
              podpowiedz="Trafi do dziennika jako osobne zdarzenie — po tym odtwarza się historię sprzętu."
            />
          </div>
        )}

        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Uwagi" value={dane.notes} blad={bledy.notes}
            onChange={(e) => setDane({ ...dane, notes: e.target.value })} />
        </div>

        {widziCeny && egzemplarz?.purchaseValue && (
          <p className="text-sm text-kamien-500 sm:col-span-2 lg:col-span-3">
            Wartość ewidencyjna: {zlote(egzemplarz.purchaseValue)}
          </p>
        )}

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : egzemplarz ? 'Zapisz zmiany' : 'Dodaj egzemplarz'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
